import api from '../../lib/api';
import {
  SurveyResponsesResponse,
  SurveyResponsesFilters,
  SurveyComparison,
  SurveyResponseFeedback,
} from './types';

/** Máximo permitido por el backend (assessments.validation getResponses) */
const MAX_PAGE_SIZE = 200;

export const getSurveyResponses = async (
  surveyId: string,
  filters?: Omit<SurveyResponsesFilters, 'surveyId'>
): Promise<SurveyResponsesResponse> => {
  const { page = 1, pageSize = 20, formId, sortBy = 'createdAt', sortDir = 'desc' } = filters || {};
  const cappedPageSize = Math.min(pageSize, MAX_PAGE_SIZE);
  const queryParams: Record<string, any> = { page, pageSize: cappedPageSize, sortBy, sortDir };

  if (formId) queryParams.formId = formId;

  const { data } = await api.get<{ success: boolean; data: SurveyResponsesResponse['data']; pagination: SurveyResponsesResponse['pagination'] }>(
    `assessments/${surveyId}/responses`,
    { params: queryParams }
  );

  return {
    data: data.data,
    pagination: data.pagination,
  };
};

/**
 * Obtiene todas las respuestas haciendo peticiones paginadas (el API limita pageSize a 200).
 */
export const getAllSurveyResponses = async (
  surveyId: string,
  filters?: Omit<SurveyResponsesFilters, 'surveyId' | 'page' | 'pageSize'>
): Promise<SurveyResponsesResponse> => {
  let page = 1;
  const pageSize = MAX_PAGE_SIZE;
  const allData: SurveyResponsesResponse['data'] = [];
  let total = 0;
  let pages = 1;

  while (page <= pages) {
    const res = await getSurveyResponses(surveyId, { ...filters, page, pageSize });
    allData.push(...res.data);
    total = res.pagination.total;
    pages = res.pagination.pages || 1;
    page += 1;
  }

  return {
    data: allData,
    pagination: {
      page: 1,
      pageSize: allData.length,
      total,
      pages: 1,
    },
  };
};

export const getSurveyComparison = async (
  surveyId: string,
  groupBy: 'year' | 'month' | 'quarter' = 'year'
): Promise<SurveyComparison> => {
  const { data } = await api.get<{ success: boolean; data: SurveyComparison }>(
    `assessments/${surveyId}/responses/comparison`,
    { params: { groupBy } }
  );
  return data.data;
};

export const getResponseFeedback = async (
  surveyId: string,
  responseId: string,
): Promise<SurveyResponseFeedback[]> => {
  const { data } = await api.get<{ success: boolean; data: SurveyResponseFeedback[] }>(
    `assessments/${surveyId}/responses/${responseId}/feedback`,
  );
  return data.data;
};

export const createResponseFeedback = async (
  surveyId: string,
  responseId: string,
  payload: { message: string; actionPlan?: string | null },
): Promise<SurveyResponseFeedback> => {
  const { data } = await api.post<{ success: boolean; data: SurveyResponseFeedback }>(
    `assessments/${surveyId}/responses/${responseId}/feedback`,
    payload,
  );
  return data.data;
};

export const loadResponseAsNonConformity = async (
  surveyId: string,
  responseId: string,
  payload: { formVersionId: string; description?: string; incidentDate?: string },
): Promise<{ record: { id: string; openingRecordId?: string | null; status: string }; openingRecordId: string }> => {
  const { data } = await api.post<{ success: boolean; data: { record: { id: string; openingRecordId?: string | null; status: string }; openingRecordId: string } }>(
    `assessments/${surveyId}/responses/${responseId}/load-as-nc`,
    payload,
  );
  return data.data;
};












